import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import {
    IClientWellbeing,
    describeWellbeing,
    DomainState,
    OVERALL_LABEL
} from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK, DOMAIN_STATE_COLOR } from './agencyChartTheme';
import { ClientAvatar } from './ClientAvatar';
import { ClientWellbeingHex } from './ClientWellbeingHex';

/** Three fits a desktop row and is as many as anyone reads before they stop comparing. */
const MAX_SHOWN = 3;

const useStyles = createUseStyles({
    row: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 6,
        alignItems: 'start'
    },

    card: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '12px 8px 14px',
        borderRadius: 12,
        border: '1px solid transparent',
        background: 'none',
        font: 'inherit',
        cursor: 'pointer',

        '&:hover': {
            borderColor: BRAND.tealEdge,
            background: BRAND.tealWash
        }
    },

    who: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
        maxWidth: '100%'
    },

    name: {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
        fontSize: 14,
        fontWeight: 600,
        color: BRAND.deepTeal
    },

    caption: {
        fontSize: 12.5,
        lineHeight: 1.45,
        textAlign: 'center' as const,
        color: CHART_INK.strongLabel
    },

    legend: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '8px 18px',
        marginTop: 14,
        paddingTop: 12,
        borderTop: `1px solid ${BRAND.tealEdge}`
    },

    legendItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12.5,
        color: CHART_INK.label
    },

    swatch: {
        width: 11,
        height: 11,
        borderRadius: 3,
        flexShrink: 0
    },

    dashKey: {
        width: 22,
        height: 0,
        flexShrink: 0,
        borderTop: `2px dashed ${CHART_INK.axis}`
    }
});

/** Worst first, matching the order a reader scans the three diagrams in. */
const LEGEND: DomainState[] = ['sharp', 'drifting', 'usual'];

/**
 * The clients whose recent weeks look least like their usual, as one diagram each.
 *
 * Three at a time rather than the whole book: this is the "who do I see today" answer, and a wall of
 * diagrams is a second caseload map rather than a shortlist. The honeycomb above already covers
 * everyone; this says where to start, and either one opens the same detail view.
 */
export const WellbeingRow: FC<{
    /** The whole caseload, most worrying first. */
    wellbeing: IClientWellbeing[];
    onOpenClient: (careMapId: string) => void;
}> = ({ wellbeing, onOpenClient }) => {
    const classes = useStyles();

    // A shortlist of who looks least like their usual has nothing to say about a client who cannot be
    // compared with it, so the unscored are left to the honeycomb, which names why.
    const shown = wellbeing
        .filter(client => client.overall !== 'unscored')
        .slice(0, MAX_SHOWN);
    if (shown.length === 0) {
        return null;
    }

    return (
        <div data-id="wellbeing-row">
            <div className={classes.row}>
                {shown.map(client => (
                    <button
                        key={client.careMapId}
                        type="button"
                        className={classes.card}
                        data-id="client-wellbeing-card"
                        data-overall={client.overall}
                        onClick={() => onOpenClient(client.careMapId)}
                    >
                        <span className={classes.who}>
                            <ClientAvatar careMapId={client.careMapId} clientName={client.clientName} size={24} />
                            <span className={`${classes.name} mp-mask`}>{client.clientName}</span>
                        </span>

                        <ClientWellbeingHex wellbeing={client} />

                        <span className={classes.caption}>{describeWellbeing(client)}</span>
                    </button>
                ))}
            </div>

            <div className={classes.legend}>
                {LEGEND.map(state => (
                    <span key={state} className={classes.legendItem}>
                        <span className={classes.swatch} style={{ background: DOMAIN_STATE_COLOR[state] }} />
                        {OVERALL_LABEL[state]}
                    </span>
                ))}
                <span className={classes.legendItem}>
                    <span className={classes.dashKey} />
                    Baseline: a wedge reaching the dashed circle
                </span>
            </div>
        </div>
    );
};
